import {isAbortError} from "../api/executor";
import {SearchSnapshotsApiInterface, SnapshotStatisticsApiInterface} from "../api/interface/search-snapshots";
import type {SnapshotStatisticsInterval, SnapshotStatisticsPeriod} from "../api/payload-types/snapshot-search";
import {generateDefaultQueryError} from "../util/errorUtil";
import {selectCredentials} from "./authSlice";
import type {RevaThunkAction} from "./revaHooks";
import {
    clearStatisticsCache,
    SNAPSHOT_STATISTICS_PERIODS,
    SNAPSHOTS_PAGE_SIZE,
    type SnapshotScope,
    selectSnapshotsInitialized,
    selectSnapshotsPageIndex,
    selectStatisticsCache,
    selectStatisticsInterval,
    setPageLoaded,
    setPageLoading,
    setQueryError,
    setStatisticsError,
    setStatisticsInterval,
    setStatisticsLoaded,
    setStatisticsLoading,
} from "./searchSnapshotsSlice";
import {selectFreeText} from "./searchValuationsSlice";

// AbortController der jeweils letzten Snapshot-Listen-Anfrage PRO Scope: eine neue Anfrage (z.B. durch
// einen neuen Freitext-Tick) bricht die vorherige noch laufende desselben Scopes ab.
const snapshotAbortControllers = new Map<SnapshotScope, AbortController>();

/**
 * #19113: Initialize a snapshot list tab (own DONE tab or Admin "Historie" sub-tab) on first
 * activation: load the first snapshot page of the given scope. A no-op if that scope's list is
 * already initialized — each scope keeps its data, so switching own ↔ Admin never re-fetches.
 * Statistics are NOT loaded here — SnapshotStatisticsPanel initializes them on mount via
 * {@link initializeSnapshotStatisticsThunk}.
 * @param scope which snapshots to load — "own" or "all"
 */
export const initializeSnapshotsThunk =
    (scope: SnapshotScope): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState) => {
        if (selectSnapshotsInitialized(getState(), scope)) {
            return;
        }
        await dispatch(loadSnapshotsPageThunk(scope, 0));
    };

/**
 * #19113: Make sure the statistics of the given scope are loaded for its currently selected
 * interval; a no-op if that interval is already cached. Dispatched by SnapshotStatisticsPanel on mount
 * (own DONE tab behind its stats toggle, Admin statistics sub-tab).
 * @param scope which snapshots' statistics to load — "own" or "all"
 */
export const initializeSnapshotStatisticsThunk =
    (scope: SnapshotScope): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState) => {
        const interval = selectStatisticsInterval(getState(), scope);
        if (!selectStatisticsCache(getState(), scope)[interval]) {
            await dispatch(loadSnapshotStatisticsThunk(scope, interval));
        }
    };

/**
 * Load one page of the snapshot list.
 * @param scope which snapshots to load — "own" or "all"
 * @param pageIndex zero-based page to load
 */
export const loadSnapshotsPageThunk =
    (scope: SnapshotScope, pageIndex: number): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {serviceApi}) => {
        dispatch(setPageLoading({scope, pageIndex}));
        // Vorherige (evtl. noch laufende) Anfrage dieses Scopes abbrechen (schneller Freitext-Tick).
        snapshotAbortControllers.get(scope)?.abort();
        const controller = new AbortController();
        snapshotAbortControllers.set(scope, controller);
        // Globaler Live-Freitextfilter (liegt in der searchValuations-Slice) auch für die Snapshots.
        const freeText = selectFreeText(getState());
        const credentials = selectCredentials(getState());
        try {
            const response = await new SearchSnapshotsApiInterface(
                {pageIndex, pageSize: SNAPSHOTS_PAGE_SIZE, filterFreeText: freeText},
                scope === "all",
                controller.signal,
            ).execute(serviceApi, credentials!);
            // Ignore the response if another page request was started in the meantime
            if (selectSnapshotsPageIndex(getState(), scope) === pageIndex) {
                dispatch(setPageLoaded({scope, result: response}));
            }
        } catch (err) {
            // Abgebrochene Anfrage (durch einen neueren Freitext-Tick überholt) still schlucken.
            if (isAbortError(err)) {
                return;
            }
            dispatch(setQueryError({scope, error: generateDefaultQueryError(err as Error)}));
        }
    };

/**
 * Load the snapshot statistics for the given interval (bucket size).
 * @param scope which snapshots' statistics to load — "own" or "all"
 * @param interval bucket size to load
 */
export const loadSnapshotStatisticsThunk =
    (scope: SnapshotScope, interval: SnapshotStatisticsInterval): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {serviceApi}) => {
        dispatch(setStatisticsLoading(scope));
        const credentials = selectCredentials(getState());
        try {
            const response = await new SnapshotStatisticsApiInterface(
                interval,
                SNAPSHOT_STATISTICS_PERIODS[interval],
                scope === "all",
            ).execute(serviceApi, credentials!);
            // TODO #19113: Fallbacks entfernen, sobald reva-rest die All-Time-Felder überall
            // (inkl. LIVE) liefert — bis dahin werden sie aus dem geladenen Fenster angenähert.
            const sumOf = (values: (p: SnapshotStatisticsPeriod) => number) =>
                response.periods.reduce((sum, p) => sum + values(p), 0);
            dispatch(
                setStatisticsLoaded({
                    scope,
                    interval,
                    statistics: {
                        periods: response.periods,
                        periodsSinceFirst: response.periodsSinceFirst ?? 0,
                    },
                    totals: {
                        firstSnapshotDate: response.firstSnapshotDate ?? null,
                        totalPreFinalCount: response.totalPreFinalCount ?? sumOf((p) => p.preFinalCount),
                        totalFinalCount: response.totalFinalCount ?? sumOf((p) => p.finalCount),
                    },
                }),
            );
        } catch (err) {
            dispatch(setStatisticsError({scope, error: generateDefaultQueryError(err as Error)}));
        }
    };

/**
 * Switch the statistics to another bucket size; loads the data on first use of the interval
 * (afterwards it is served from the slice cache).
 * @param scope which scope's statistics to switch — "own" or "all"
 * @param interval bucket size to switch to
 */
export const changeStatisticsIntervalThunk =
    (scope: SnapshotScope, interval: SnapshotStatisticsInterval): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState) => {
        dispatch(setStatisticsInterval({scope, interval}));
        if (!selectStatisticsCache(getState(), scope)[interval]) {
            await dispatch(loadSnapshotStatisticsThunk(scope, interval));
        }
    };

/**
 * Reload a scope's snapshot data, but only what was actually loaded: the current list page (if the
 * list is initialized) and the current interval's statistics (if they were loaded — the cache of
 * all other intervals is dropped either way, so they are fetched fresh on next use). A closed,
 * never-opened statistics panel therefore triggers no statistics request; clearing the cache alone
 * guarantees freshness on the next open.
 * @param scope which scope to reload — "own" or "all"
 */
export const reloadSnapshotsThunk =
    (scope: SnapshotScope): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState) => {
        const state = getState();
        const interval = selectStatisticsInterval(state, scope);
        const statisticsLoaded = !!selectStatisticsCache(state, scope)[interval];
        dispatch(clearStatisticsCache(scope));
        const tasks: Promise<unknown>[] = [];
        if (selectSnapshotsInitialized(state, scope)) {
            tasks.push(dispatch(loadSnapshotsPageThunk(scope, selectSnapshotsPageIndex(state, scope))));
        }
        if (statisticsLoaded) {
            tasks.push(dispatch(loadSnapshotStatisticsThunk(scope, interval)));
        }
        await Promise.all(tasks);
    };
